/**
 * ZFS Calculator - Preset Configurations
 *
 * Pre-configured scenarios for common use cases
 */

import type { PoolScenario } from '../types';

/**
 * Preset categories with metadata
 */
export const PRESET_CATEGORIES = {
  homelab: {
    label: 'Homelab',
    description: 'Used enterprise drives for budget-conscious homelab builders',
    icon: '🏠',
  },
  backup: {
    label: 'Backup Server',
    description: 'High-redundancy configurations for critical data',
    icon: '💾',
  },
  media: {
    label: 'Media Server',
    description: 'Capacity-optimized for media storage',
    icon: '🎬',
  },
  performance: {
    label: 'Performance',
    description: 'Low-latency configurations for databases and VMs',
    icon: '⚡',
  },
  budget: {
    label: 'Budget',
    description: 'Cost-effective configurations',
    icon: '💰',
  },
  custom: {
    label: 'Custom',
    description: 'User-saved custom configurations',
    icon: '🔧',
  },
} as const;

/**
 * Helper to create a preset with common defaults
 */
function createPreset(
  id: string,
  name: string,
  category: PoolScenario['category'],
  description: string,
  topology: PoolScenario['topology'],
  drive: Partial<PoolScenario['drive']> & { driveSizeTB: number; driveCost: number },
  utilization?: Partial<PoolScenario['utilization']>,
  economics?: Partial<PoolScenario['economics']>
): PoolScenario {
  return {
    id,
    name,
    category,
    description,
    topology,
    drive: {
      wattsIdle: 6,
      wattsLoad: 9,
      afr: 0.35,
      peakSequentialMBps: 260,
      driveIOPS: 90,
      driveType: 'enterprise',
      ...drive,
    },
    zfs: {
      ashift: 12,
      slopPercent: 3.125,
      recordsize: 128,
      compressionRatio: 1.0,
    },
    utilization: {
      poolFillPercent: 80,
      freeSpaceLimitPercent: 20,
      scrubIntervalDays: 30,
      ...utilization,
    },
    economics: {
      electricityRate: 0.12,
      uptimeHoursPerDay: 24,
      ...economics,
    },
    performance: {
      workloadType: 'mixed',
      readPercentage: 0.60,
    },
  };
}

export const PRESETS: PoolScenario[] = [
  // ==========================================================================
  // HOMELAB PRESETS (Used Enterprise Drives)
  // ==========================================================================

  createPreset(
    'preset-homelab-8tb-used-3yr',
    'Homelab: 8× 8TB Used (3 years old)',
    'homelab',
    'Budget-friendly used drives. 3-year-old enterprise drives still have stable AFR (~1%). Good for homelabs on a budget.',
    { raidLevel: 'raidz2', drivesPerVdev: 8, vdevCount: 1 },
    { driveSizeTB: 8, driveCost: 80, driveAge: 3, afr: 1.05 }
  ),

  createPreset(
    'preset-homelab-8tb-used-5yr',
    'Homelab: 8× 8TB Used (5 years old)',
    'homelab',
    'Older used drives entering elevated failure zone. Budget-friendly but monitor closely. Consider spares.',
    { raidLevel: 'raidz2', drivesPerVdev: 8, vdevCount: 1 },
    { driveSizeTB: 8, driveCost: 60, driveAge: 5, afr: 0.97 }
  ),

  createPreset(
    'preset-homelab-12tb-used-5yr',
    'Homelab: 6× 12TB Used (5 years old)',
    'homelab',
    'Sweet spot for capacity:cost. Used 12TB drives offer great value. Plan for replacements as drives age.',
    { raidLevel: 'raidz2', drivesPerVdev: 6, vdevCount: 1 },
    { driveSizeTB: 12, driveCost: 100, driveAge: 5, afr: 0.97 }
  ),

  createPreset(
    'preset-homelab-10tb-used-3yr-raidz3',
    'Homelab: 10× 10TB Used (3 years old, RAIDZ3)',
    'homelab',
    'Maximum redundancy for peace of mind with used drives. RAIDZ3 tolerates 3 drive failures.',
    { raidLevel: 'raidz3', drivesPerVdev: 10, vdevCount: 1 },
    { driveSizeTB: 10, driveCost: 90, driveAge: 3, afr: 1.05 }
  ),

  createPreset(
    'preset-homelab-4tb-used-7yr',
    'Homelab: 6× 4TB Used (7 years old)',
    'homelab',
    'Very old drives for non-critical data. High failure risk - use for scratch/storage with backups elsewhere.',
    { raidLevel: 'raidz2', drivesPerVdev: 6, vdevCount: 1 },
    { driveSizeTB: 4, driveCost: 25, driveAge: 7, afr: 1.24 }
  ),

  // ==========================================================================
  // BACKUP SERVER PRESETS (High Redundancy)
  // ==========================================================================

  createPreset(
    'preset-backup-raidz3-12tb',
    'Backup: 8× 12TB RAIDZ3',
    'backup',
    'Critical backup server with triple parity. RAIDZ3 ensures data survives 3 simultaneous drive failures.',
    { raidLevel: 'raidz3', drivesPerVdev: 8, vdevCount: 1 },
    { driveSizeTB: 12, driveCost: 220 },
    { poolFillPercent: 70, scrubIntervalDays: 7 }
  ),

  createPreset(
    'preset-backup-mirror-8tb',
    'Backup: 6× 8TB Mirrors (3 pairs)',
    'backup',
    'Fast resilver and excellent redundancy. Mirror-based backup server with quick recovery times.',
    { raidLevel: 'mirror', drivesPerVdev: 2, vdevCount: 3 },
    { driveSizeTB: 8, driveCost: 150 },
    { poolFillPercent: 70, scrubIntervalDays: 7 }
  ),

  createPreset(
    'preset-backup-raidz2-16tb',
    'Backup: 6× 16TB RAIDZ2',
    'backup',
    'Large-scale backup with dual parity. Good balance of capacity and reliability for backup workloads.',
    { raidLevel: 'raidz2', drivesPerVdev: 6, vdevCount: 1 },
    { driveSizeTB: 16, driveCost: 280 },
    { poolFillPercent: 70, scrubIntervalDays: 14 }
  ),

  createPreset(
    'preset-backup-cold-storage',
    'Backup: 12× 18TB Cold Storage',
    'backup',
    'High-capacity cold storage. RAIDZ2 with weekly scrubs for archival backup data.',
    { raidLevel: 'raidz2', drivesPerVdev: 12, vdevCount: 1 },
    { driveSizeTB: 18, driveCost: 300 },
    { poolFillPercent: 90, scrubIntervalDays: 7 }
  ),

  // ==========================================================================
  // MEDIA SERVER PRESETS (Capacity Optimized)
  // ==========================================================================

  createPreset(
    'preset-media-raidz2-12tb',
    'Media: 8× 12TB RAIDZ2',
    'media',
    'Balanced media server. Good capacity with RAIDZ2 protection. Recordsize 1M for large video files.',
    { raidLevel: 'raidz2', drivesPerVdev: 8, vdevCount: 1 },
    { driveSizeTB: 12, driveCost: 200 },
    { poolFillPercent: 85, scrubIntervalDays: 30 }
  ),

  createPreset(
    'preset-media-raidz2-16tb',
    'Media: 6× 16TB RAIDZ2',
    'media',
    'Large media library. 16TB drives maximize capacity while maintaining redundancy.',
    { raidLevel: 'raidz2', drivesPerVdev: 6, vdevCount: 1 },
    { driveSizeTB: 16, driveCost: 280 },
    { poolFillPercent: 85, scrubIntervalDays: 30 }
  ),

  createPreset(
    'preset-media-raidz2-20tb',
    'Media: 6× 20TB RAIDZ2',
    'media',
    'Maximum density media server. 20TB drives for massive media collections.',
    { raidLevel: 'raidz2', drivesPerVdev: 6, vdevCount: 1 },
    { driveSizeTB: 20, driveCost: 350 },
    { poolFillPercent: 85, scrubIntervalDays: 30 }
  ),

  createPreset(
    'preset-media-2-vdev-12tb',
    'Media: 12× 12TB (2× RAIDZ2 vdevs)',
    'media',
    'High-throughput media server with 2 RAIDZ2 vdevs. Better performance for concurrent streaming.',
    { raidLevel: 'raidz2', drivesPerVdev: 6, vdevCount: 2 },
    { driveSizeTB: 12, driveCost: 200 },
    { poolFillPercent: 80, scrubIntervalDays: 30 }
  ),

  createPreset(
    'preset-media-compression',
    'Media: 8× 14TB RAIDZ2 + LZ4',
    'media',
    'Media server with compression enabled. Achieves ~30% more effective capacity for compressible media.',
    { raidLevel: 'raidz2', drivesPerVdev: 8, vdevCount: 1 },
    { driveSizeTB: 14, driveCost: 240 },
    { poolFillPercent: 85, scrubIntervalDays: 30 }
  ),

  // ==========================================================================
  // PERFORMANCE PRESETS (Low Latency)
  // ==========================================================================

  createPreset(
    'preset-perf-mirror-ssd',
    'Performance: 4× 2TB NVMe Mirrors',
    'performance',
    'Blazing fast database/VM storage. NVMe mirrors with recordsize 8K for databases.',
    { raidLevel: 'mirror', drivesPerVdev: 2, vdevCount: 2 },
    { driveSizeTB: 2, driveCost: 180, wattsIdle: 3, wattsLoad: 6, peakSequentialMBps: 3000, driveIOPS: 30000, driveType: 'enterprise' },
    { poolFillPercent: 60, scrubIntervalDays: 7 }
  ),

  createPreset(
    'preset-perf-mirror-10k',
    'Performance: 6× 900GB 10K SAS Mirrors',
    'performance',
    'Enterprise SAS drives in mirrors. Low latency for transactional databases.',
    { raidLevel: 'mirror', drivesPerVdev: 2, vdevCount: 3 },
    { driveSizeTB: 0.9, driveCost: 150, wattsIdle: 5, wattsLoad: 10, peakSequentialMBps: 220, driveIOPS: 180 },
    { poolFillPercent: 60, scrubIntervalDays: 14 }
  ),

  createPreset(
    'preset-perf-raidz10',
    'Performance: 8× 4TB RAIDZ10',
    'performance',
    'RAIDZ10 (nested) for balance of performance and capacity. Good for VM storage.',
    { raidLevel: 'raidz2', drivesPerVdev: 4, vdevCount: 2 },
    { driveSizeTB: 4, driveCost: 140, wattsIdle: 5, wattsLoad: 8, peakSequentialMBps: 240 },
    { poolFillPercent: 70, scrubIntervalDays: 14 }
  ),

  createPreset(
    'preset-perf-database',
    'Performance: 6× 8TB Enterprise Database',
    'performance',
    'Optimized for database workloads. Recordsize 16K, ashift 12, 80% reads.',
    { raidLevel: 'raidz2', drivesPerVdev: 6, vdevCount: 1 },
    { driveSizeTB: 8, driveCost: 180 },
    { poolFillPercent: 70, scrubIntervalDays: 14 }
  ),

  createPreset(
    'preset-perf-vm-storage',
    'Performance: 10× 1TB NVMe (Mixed)',
    'performance',
    'Mixed NVMe pool for VMs. Small recordsize for random I/O workloads.',
    { raidLevel: 'raidz2', drivesPerVdev: 5, vdevCount: 2 },
    { driveSizeTB: 1, driveCost: 120, wattsIdle: 3, wattsLoad: 7, peakSequentialMBps: 3500, driveIOPS: 50000 },
    { poolFillPercent: 60, scrubIntervalDays: 7 }
  ),

  // ==========================================================================
  // BUDGET PRESETS (Cost Effective)
  // ==========================================================================

  createPreset(
    'preset-budget-raidz1-8tb',
    'Budget: 5× 8TB RAIDZ1',
    'budget',
    'Most cost-effective config. RAIDZ1 sacrifices some redundancy for lower upfront cost.',
    { raidLevel: 'raidz1', drivesPerVdev: 5, vdevCount: 1 },
    { driveSizeTB: 8, driveCost: 120, afr: 1.2, driveType: 'consumer' },
    { poolFillPercent: 85 }
  ),

  createPreset(
    'preset-budget-raidz2-6tb',
    'Budget: 6× 6TB RAIDZ2',
    'budget',
    'Budget-friendly RAIDZ2 with smaller drives. Good balance of cost and reliability.',
    { raidLevel: 'raidz2', drivesPerVdev: 6, vdevCount: 1 },
    { driveSizeTB: 6, driveCost: 100, afr: 1.5, driveType: 'consumer' },
    { poolFillPercent: 85 }
  ),

  createPreset(
    'preset-budget-low-power',
    'Budget: 4× 4TB Low Power',
    'budget',
    'Minimal power consumption. 4 drive config for low electricity costs.',
    { raidLevel: 'raidz2', drivesPerVdev: 4, vdevCount: 1 },
    { driveSizeTB: 4, driveCost: 80, wattsIdle: 3.5, wattsLoad: 5.5, afr: 1.5, driveType: 'consumer' },
    { poolFillPercent: 85 }
  ),

  createPreset(
    'preset-budget-part-time',
    'Budget: 6× 8TB Part-Time Backup',
    'budget',
    'Backup server that spins 12h/day. Reduces power costs for non-critical backups.',
    { raidLevel: 'raidz2', drivesPerVdev: 6, vdevCount: 1 },
    { driveSizeTB: 8, driveCost: 120, afr: 1.2, driveType: 'consumer' },
    { poolFillPercent: 90 },
    { uptimeHoursPerDay: 12 }
  ),
];

// Fix up the presets that need specific tuning
export const PRESETS_FIXED: PoolScenario[] = PRESETS.map(preset => {
  const updated = { ...preset };

  // Database workloads
  if (preset.id === 'preset-perf-database' || preset.id === 'preset-perf-mirror-10k') {
    updated.zfs.recordsize = 16;
    updated.performance.workloadType = 'database';
    updated.performance.readPercentage = 0.80;
  }

  // VM workloads
  if (preset.id === 'preset-perf-vm-storage' || preset.id === 'preset-perf-mirror-ssd') {
    updated.zfs.recordsize = 8;
    updated.performance.workloadType = 'database';
    updated.performance.readPercentage = 0.70;
  }

  // Media workloads
  if (preset.category === 'media') {
    updated.zfs.recordsize = 1024;
    updated.performance.workloadType = 'media';
    updated.performance.readPercentage = 0.90;
  }

  // Backup workloads
  if (preset.category === 'backup') {
    updated.performance.workloadType = 'backup';
    updated.performance.readPercentage = 0.80;
  }

  // Special handling for compression preset
  if (preset.id === 'preset-media-compression') {
    updated.zfs.compressionRatio = 1.3;
  }

  return updated;
});

/**
 * Get preset by ID
 */
export function getPresetById(id: string): PoolScenario | undefined {
  return PRESETS_FIXED.find(p => p.id === id);
}

/**
 * Get presets by category
 */
export function getPresetsByCategory(category: PoolScenario['category']): PoolScenario[] {
  return PRESETS_FIXED.filter(p => p.category === category);
}

/**
 * Get custom presets from localStorage
 */
export function getCustomPresets(): PoolScenario[] {
  try {
    const stored = localStorage.getItem('zfs-calculator-custom-presets');
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

/**
 * Save custom preset to localStorage
 */
export function saveCustomPreset(preset: PoolScenario): void {
  const customPresets = getCustomPresets();
  const existingIndex = customPresets.findIndex(p => p.id === preset.id);

  if (existingIndex >= 0) {
    customPresets[existingIndex] = preset;
  } else {
    customPresets.push(preset);
  }

  localStorage.setItem('zfs-calculator-custom-presets', JSON.stringify(customPresets));
}

/**
 * Delete custom preset from localStorage
 */
export function deleteCustomPreset(id: string): void {
  const customPresets = getCustomPresets().filter(p => p.id !== id);
  localStorage.setItem('zfs-calculator-custom-presets', JSON.stringify(customPresets));
}

/**
 * Get all available presets (built-in + custom)
 */
export function getAllPresets(): PoolScenario[] {
  return [...PRESETS_FIXED, ...getCustomPresets()];
}
